import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Button, Paper, Stack, Typography, useMediaQuery, useTheme } from '@mui/material';
import ReactGridLayout, { WidthProvider } from 'react-grid-layout';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import ToolsBar from '../ToolsBar';
import IpListWidget from '../widgets/IpListWidget';
import JournalWidget from '../widgets/JournalWidget';
import IncidentsWidget from '../widgets/IncidentsWidget';
import PhotosWidget from '../widgets/PhotosWidget';
import ScheduleWidget from '../widgets/ScheduleWidget';
import OperationProcessWidget from '../widgets/OperationProcessWidget';
import MaterialsWidget from '../widgets/MaterialsWidget';
import MapOverviewWidget from '../widgets/MapOverviewWidget';
import NotesWidget from '../widgets/NotesWidget';
import RoutersWidget from '../widgets/RoutersWidget';
import MaterialsSummaryBar from '../widgets/MaterialsSummaryBar';
import ContactsBar from '../widgets/ContactsBar';
import SiteLinksBar from '../widgets/SiteLinksBar';
import { DashboardSection } from '../DashboardSidebar';
import ScheduleDetailDialog from '../dialogs/ScheduleDetailDialog';
import VisitLogDetailDialog from '../dialogs/VisitLogDetailDialog';
import IncidentDetailDialog from '../dialogs/IncidentDetailDialog';
import { SiteSchedule } from '../../../models/SiteSchedule';
import { SiteVisitLog } from '../../../models/SiteVisitLog';
import { SiteIncident } from '../../../models/SiteIncident';
import { SiteIncidentComment } from '../../../models/SiteIncidentComment';
import { SitePhoto } from '../../../models/SitePhoto';
import { SiteIncidentCommentService } from '../../../services/SiteIncidentCommentService';
import { SitePhotoService } from '../../../services/SitePhotoService';
import { SiteScheduleService } from '../../../services/SiteScheduleService';
import ScheduleFormDialog, {
  canEditSchedule,
} from '../../../components/dialogs/ScheduleFormDialog';
import usePermissions from '../../../hooks/usePermissions';
import { DashboardLayoutService } from '../../../services/DashboardLayoutService';
import {
  DASHBOARD_GRID_COLUMNS,
  DASHBOARD_ROW_HEIGHT,
  DEFAULT_DASHBOARD_LAYOUT,
  DashboardLayout,
  DashboardLayoutKey,
} from '../dashboardLayout';

// 편집 모드에서만 드래그/리사이즈 핸들이 보이도록 width 는 컨테이너 폭에 자동으로 맞춘다.
const DashboardGridLayout = WidthProvider(ReactGridLayout);

// Paper(variant outlined, p:2) 상하 패딩 16px×2 + 테두리 1px×2.
const BAR_FRAME_PX = 34;
// react-grid-layout 의 세로 margin ([16, 16] 의 두 번째 값).
const GRID_MARGIN_Y = 16;

interface AutoHeightBarProps {
  barKey: DashboardLayoutKey;
  onHeight: (key: DashboardLayoutKey, contentPx: number) => void;
  children: React.ReactNode;
}

/**
 * 상단 바 카드 — 내용 높이(담당자 수, 링크 줄 수 등 데이터에 따라 변함)를
 * 측정해 부모에게 알린다. 개요는 이 값으로 그리드 셀 높이를 내용에 맞춰
 * 늘리므로 카드 안에 스크롤이 생기지 않는다.
 */
const AutoHeightBar: React.FC<AutoHeightBarProps> = ({ barKey, onHeight, children }) => {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const report = () => onHeight(barKey, el.offsetHeight);
    report();
    const observer = new ResizeObserver(report);
    observer.observe(el);
    return () => observer.disconnect();
  }, [barKey, onHeight]);
  return (
    // overflow hidden — 자동 높이가 반영되기 전 한 프레임이나 편집 모드에서
    // 내용이 셀보다 클 수 있는데, 그때도 스크롤바 대신 조용히 잘리게.
    <Paper variant="outlined" sx={{ borderRadius: 2, p: 2, height: '100%', overflow: 'hidden' }}>
      <Box ref={ref}>{children}</Box>
    </Paper>
  );
};

interface OverviewSectionProps {
  projectId: string | undefined;
  projectIdNum: number;
  /** 위젯의 액션 클릭 시 사이드바 섹션을 전환하는 콜백 */
  onJumpToSection: (section: DashboardSection) => void;
}

/**
 * "개요" 섹션 — 상단 바 4개 + 하단 그리드 위젯 8개(맵 포함), 총 12블록.
 *
 * 블록 목록 (기본 노출 순서, 좌→우, 상→하):
 *   상단: 자재수량 / 현장 담당자 / 관련사이트 / 작업도구
 *   하단: 운영 프로세스 / 다가오는 일정 / 최근 출장 일지 / 장애 이력 /
 *        IP 리스트 / 이미지 보관함 / 자재 현황 / 맵
 *
 * admin/editor 는 "편집" 모드에서 12블록의 위치(x/y/w/h)를 자유롭게 바꿔 저장할
 * 수 있다 — 위젯을 지우거나 새로 추가하는 기능은 없다. 저장된 배치는
 * projects.dashboard_layout 에 보관되어 전원에게 동일하게 노출된다.
 *
 * 위젯의 "전체 보기" 액션은 곧장 해당 사이드바 섹션으로 이동한다.
 */
const OverviewSection: React.FC<OverviewSectionProps> = ({
  projectId,
  projectIdNum,
  onJumpToSection,
}) => {
  const hasProjectId = Number.isFinite(projectIdNum);
  const { canEdit } = usePermissions();
  const theme = useTheme();
  // 좁은 화면에서는 저장된 좌표 대신 오늘처럼 단순 세로 스택으로 폴백.
  const isNarrow = useMediaQuery(theme.breakpoints.down('md'));

  // 위젯 행 클릭 → 페이지 이동 없이 바로 상세 모달. 장애는 댓글/사진을
  // 모달 오픈 시점에 lazy 로드한다 (개요 진입만으로 N건 전부 당기지 않게).
  const [scheduleDetail, setScheduleDetail] = useState<SiteSchedule | null>(null);
  // 일정 수정/삭제 — 공용 ScheduleFormDialog 재사용. 저장/삭제 후에는
  // ScheduleWidget 을 key 로 리마운트해 목록을 새로고침한다.
  const writable = useMemo(() => canEditSchedule(), []);
  const [editingSchedule, setEditingSchedule] = useState<SiteSchedule | null>(null);
  const [scheduleRefresh, setScheduleRefresh] = useState(0);

  const handleEditSchedule = (s: SiteSchedule) => {
    setScheduleDetail(null);
    setEditingSchedule(s);
  };

  const handleDeleteSchedule = async (s: SiteSchedule) => {
    if (!window.confirm(`'${s.title}' 일정을 삭제하시겠습니까?`)) return;
    try {
      await SiteScheduleService.delete(s.id);
      setScheduleDetail(null);
      setScheduleRefresh((n) => n + 1);
    } catch (e) {
      window.alert(e instanceof Error ? e.message : '일정 삭제에 실패했습니다.');
    }
  };

  const [visitDetail, setVisitDetail] = useState<SiteVisitLog | null>(null);
  const [incidentDetail, setIncidentDetail] = useState<SiteIncident | null>(null);
  const [incidentComments, setIncidentComments] = useState<SiteIncidentComment[]>([]);
  const [incidentPhotos, setIncidentPhotos] = useState<SitePhoto[]>([]);

  const openIncidentDetail = (incident: SiteIncident) => {
    setIncidentComments([]);
    setIncidentPhotos([]);
    setIncidentDetail(incident);
    SiteIncidentCommentService.list(incident.id)
      .then(setIncidentComments)
      .catch(() => setIncidentComments([]));
    SitePhotoService.list(projectIdNum)
      .then((all) => setIncidentPhotos(all.filter((p) => p.related_incident_id === incident.id)))
      .catch(() => setIncidentPhotos([]));
  };

  // 위젯 배치 — 저장된 값이 로드되기 전까지는 기본 배치로 렌더링해 레이아웃이
  // 튀지 않게 한다.
  const [savedLayout, setSavedLayout] = useState<DashboardLayout>(DEFAULT_DASHBOARD_LAYOUT);
  const [draftLayout, setDraftLayout] = useState<DashboardLayout>(DEFAULT_DASHBOARD_LAYOUT);
  const [isEditing, setIsEditing] = useState(false);
  const [savingLayout, setSavingLayout] = useState(false);
  const [layoutError, setLayoutError] = useState<string | null>(null);

  useEffect(() => {
    if (!hasProjectId) return;
    let cancelled = false;
    DashboardLayoutService.get(projectIdNum)
      .then((layout) => {
        if (cancelled) return;
        setSavedLayout(layout);
        setDraftLayout(layout);
      })
      .catch(() => {
        // 조회 실패 시 기본 배치 유지 — 위젯 배치는 부가 기능이라 전체 개요 렌더를 막지 않는다.
      });
    return () => {
      cancelled = true;
    };
  }, [hasProjectId, projectIdNum]);

  const startEditing = () => {
    setLayoutError(null);
    setDraftLayout(savedLayout);
    setIsEditing(true);
  };

  const cancelEditing = () => {
    setDraftLayout(savedLayout);
    setIsEditing(false);
    setLayoutError(null);
  };

  const saveLayout = async () => {
    setSavingLayout(true);
    setLayoutError(null);
    try {
      await DashboardLayoutService.update(projectIdNum, draftLayout);
      setSavedLayout(draftLayout);
      setIsEditing(false);
    } catch (e) {
      setLayoutError(e instanceof Error ? e.message : '위젯 배치 저장 실패');
    } finally {
      setSavingLayout(false);
    }
  };

  const missing = (
    <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, height: '100%' }}>
      <Typography variant="caption">프로젝트 ID 없음</Typography>
    </Paper>
  );

  // 상단 바 4개의 내용 기준 필요 셀 높이(h). AutoHeightBar 가 측정한 px 를
  // react-grid-layout 의 셀 픽셀 공식(h*rowHeight + (h-1)*margin)으로 역산한다.
  const [autoBarH, setAutoBarH] = useState<Partial<Record<DashboardLayoutKey, number>>>({});
  const handleBarHeight = useCallback((key: DashboardLayoutKey, contentPx: number) => {
    const cellPx = contentPx + BAR_FRAME_PX;
    const needed = Math.max(
      2,
      Math.ceil((cellPx + GRID_MARGIN_Y) / (DASHBOARD_ROW_HEIGHT + GRID_MARGIN_Y)),
    );
    setAutoBarH((prev) => (prev[key] === needed ? prev : { ...prev, [key]: needed }));
  }, []);

  // key → 실제 렌더링할 내용. 12개 key 는 고정 세트 — 여기서 새 항목을 추가하거나
  // 지우는 UI 는 없다 (dashboardLayout.ts 의 DASHBOARD_LAYOUT_KEYS 참고).
  const blocks: Record<DashboardLayoutKey, React.ReactNode> = {
    'materials-summary': hasProjectId ? (
      <AutoHeightBar barKey="materials-summary" onHeight={handleBarHeight}>
        <MaterialsSummaryBar projectId={projectIdNum} />
      </AutoHeightBar>
    ) : (
      missing
    ),
    contacts: hasProjectId ? (
      <AutoHeightBar barKey="contacts" onHeight={handleBarHeight}>
        <ContactsBar projectId={projectIdNum} />
      </AutoHeightBar>
    ) : (
      missing
    ),
    'site-links': hasProjectId ? (
      <AutoHeightBar barKey="site-links" onHeight={handleBarHeight}>
        <SiteLinksBar projectId={projectIdNum} />
      </AutoHeightBar>
    ) : (
      missing
    ),
    tools: projectId ? (
      <AutoHeightBar barKey="tools" onHeight={handleBarHeight}>
        <ToolsBar projectId={projectId} />
      </AutoHeightBar>
    ) : (
      missing
    ),
    'operation-process': hasProjectId ? (
      <OperationProcessWidget projectId={projectIdNum} />
    ) : (
      missing
    ),
    schedule: hasProjectId ? (
      <ScheduleWidget
        key={scheduleRefresh}
        projectId={projectIdNum}
        onActionClick={() => onJumpToSection('schedules')}
        onItemClick={setScheduleDetail}
      />
    ) : (
      missing
    ),
    journal: hasProjectId ? (
      <JournalWidget
        projectId={projectIdNum}
        onActionClick={() => onJumpToSection('journal')}
        onItemClick={setVisitDetail}
      />
    ) : (
      missing
    ),
    incidents: hasProjectId ? (
      <IncidentsWidget projectId={projectIdNum} onItemClick={openIncidentDetail} />
    ) : (
      missing
    ),
    'ip-list': hasProjectId ? (
      <IpListWidget projectId={projectIdNum} onActionClick={() => onJumpToSection('network')} />
    ) : (
      missing
    ),
    photos: hasProjectId ? <PhotosWidget projectId={projectIdNum} /> : missing,
    'materials-status': hasProjectId ? <MaterialsWidget projectId={projectIdNum} /> : missing,
    map: hasProjectId ? <MapOverviewWidget projectId={projectIdNum} /> : missing,
    notes: hasProjectId ? (
      <NotesWidget projectId={projectIdNum} onActionClick={() => onJumpToSection('schedules')} />
    ) : (
      missing
    ),
  };

  // 보기 모드에서는 상단 바 셀 높이를 측정값으로 덮어써 내용이 항상 다 보이게
  // 한다(카드 안 스크롤 제거). 편집 모드에서는 드래그/리사이즈와 충돌하지
  // 않도록 저장값 그대로 — 저장하고 나오면 다시 자동 높이가 적용된다.
  const activeLayout = isEditing
    ? draftLayout
    : savedLayout.map((item) => {
        const auto = autoBarH[item.i];
        return auto != null && auto !== item.h ? { ...item, h: auto } : item;
      });

  return (
    <>
      {canEdit && !isNarrow && (
        <Stack
          direction="row"
          justifyContent="flex-end"
          alignItems="center"
          spacing={1}
          sx={{ mb: 1.5 }}
        >
          {layoutError && (
            <Typography variant="caption" color="warning.main">
              {layoutError}
            </Typography>
          )}
          {!isEditing ? (
            <Button size="small" variant="outlined" onClick={startEditing}>
              편집
            </Button>
          ) : (
            <>
              <Typography variant="caption" color="text.secondary">
                드래그로 위치를, 모서리를 끌어 크기를 바꿀 수 있습니다.
              </Typography>
              <Button size="small" onClick={cancelEditing} disabled={savingLayout}>
                취소
              </Button>
              <Button size="small" variant="contained" onClick={saveLayout} disabled={savingLayout}>
                {savingLayout ? '저장 중…' : '저장'}
              </Button>
            </>
          )}
        </Stack>
      )}

      {hasProjectId && <RoutersWidget projectId={projectIdNum} />}

      {isNarrow ? (
        <Stack spacing={2} sx={{ mb: 2 }}>
          {DEFAULT_DASHBOARD_LAYOUT.map((item) => (
            <Box key={item.i}>{blocks[item.i]}</Box>
          ))}
        </Stack>
      ) : (
        <Box sx={{ mb: 2 }}>
          <DashboardGridLayout
            className="layout"
            layout={activeLayout}
            cols={DASHBOARD_GRID_COLUMNS}
            rowHeight={DASHBOARD_ROW_HEIGHT}
            margin={[16, 16]}
            isDraggable={isEditing}
            isResizable={isEditing}
            onLayoutChange={(next) => {
              if (isEditing) setDraftLayout(next as DashboardLayout);
            }}
          >
            {activeLayout.map((item) => (
              <div key={item.i}>{blocks[item.i]}</div>
            ))}
          </DashboardGridLayout>
        </Box>
      )}

      {/* 위젯 행 클릭 상세 모달 — 권한이 있으면 수정/삭제 가능 */}
      <ScheduleDetailDialog
        open={scheduleDetail != null}
        schedule={scheduleDetail}
        onClose={() => setScheduleDetail(null)}
        onEdit={writable ? handleEditSchedule : undefined}
        onDelete={writable ? handleDeleteSchedule : undefined}
      />
      <ScheduleFormDialog
        open={editingSchedule != null}
        editing={editingSchedule}
        onClose={() => setEditingSchedule(null)}
        onSaved={() => {
          setEditingSchedule(null);
          setScheduleRefresh((n) => n + 1);
        }}
        projectId={projectIdNum}
      />
      <VisitLogDetailDialog
        open={visitDetail != null}
        log={visitDetail}
        onClose={() => setVisitDetail(null)}
      />
      <IncidentDetailDialog
        open={incidentDetail != null}
        incident={incidentDetail}
        comments={incidentComments}
        photos={incidentPhotos}
        projectId={projectIdNum}
        onClose={() => setIncidentDetail(null)}
        onCommentCreated={(c) => setIncidentComments((prev) => [...prev, c])}
        onPhotoUploaded={(p) => setIncidentPhotos((prev) => [...prev, p])}
        onIncidentUpdated={setIncidentDetail}
      />
    </>
  );
};

export default OverviewSection;
